import { useState, useContext, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { RETURN_PATH_KEY } from '../../util/requireAuth';
import jwt_decode from 'jwt-decode';
import { startAuthentication } from '@simplewebauthn/browser';
import { toast } from 'react-toastify';

// IMPORT HOOKS, COMPONENTS AND OTHER FUNCTIONS
import AuthContext from '../../context/authContext';
import useHttp from '../../hooks/useHttp';
import useWebAuthnApi from '../../hooks/useWebAuthnApi';
import DeviceConfirmation from '../components/Login/DeviceConfirmation';
import FunZoneLogo from '../components/Login/FunZoneLogo';
import CircularProgress from '@mui/material/CircularProgress';

// IMPORT MUI COMPONENTS
import Grid from '@mui/material/Unstable_Grid2';
import { Button, TextField, Alert, Box } from '@mui/material';
import { faArrowRight, faKey } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LegalLinks from '../components/LegalLinks';
import { getLoginFormGridSx } from '../../util/loginFormGridSx';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import { getDeviceInstallId, requestDeviceTokenStorageKey } from '../../util/deviceInfo';

const isNetworkError = err => {
  const msg = typeof err?.message === 'string' ? err.message : '';
  return (
    err?.name === 'TypeError' ||
    msg.includes('Failed to fetch') ||
    msg.toLowerCase().includes('networkerror')
  );
};

function Login() {
  const authCtx = useContext(AuthContext);
  const returnPathRef = useRef(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [needsDeviceConfirmation, setNeedsDeviceConfirmation] = useState(false);
  const [allowedDeviceTypes, setAllowedDeviceTypes] = useState([]);
  const [loginWarning, setLoginWarning] = useState(null); // { remainingAttempts: number }
  const [accountLocked, setAccountLocked] = useState(null); // { lockedUntil: string }
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const { error, sendRequest: login, isLoading } = useHttp();
  const { webauthnLoginStart, webauthnLoginFinish } = useWebAuthnApi();

  const getDaysRemaining = gracePeriodEndsAt => {
    if (!gracePeriodEndsAt) return 0;
    const diff = new Date(gracePeriodEndsAt).getTime() - Date.now();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (24 * 60 * 60 * 1000));
  };

  // Check if we should redirect to RFID login
  const intermediateTokens = authCtx.intermediateTokens;
  const hasIntermediateTokens = Object.keys(intermediateTokens).length > 0;
  const hasDeviceToken = !!authCtx.deviceToken;

  // If on counter device (has device token) and have intermediate tokens, redirect to RFID login
  if (hasDeviceToken && hasIntermediateTokens) {
    return <Navigate to='/rfid-login' replace={true} />;
  }

  const continueLoginFlow = async loginResult => {
    // The backend can no longer honour the device token this browser stored — it
    // expired, or its registration was revoked. Dropping it here is what lets the
    // device be confirmed or activated again; left in place it would silently hand
    // out private rights on every following login.
    if (loginResult.deviceTokenInvalid) {
      authCtx.clearDeviceToken?.();
      toast.warning(
        'Die Gerätefreigabe dieses Geräts ist abgelaufen oder wurde entzogen. Der Gerätetyp muss neu bestätigt oder angefragt werden.'
      );
    }
    if (loginResult.needsDeviceConfirmation) {
      setNeedsDeviceConfirmation(true);
      setAllowedDeviceTypes(loginResult.allowedDeviceTypes);
      // store token and intermediate token temporarily but don't set as logged in yet
      authCtx.setTempToken(loginResult.token, loginResult.intermediateToken);
      return;
    }

    // The backend activates a device type request that was approved in the
    // meantime as part of this login and hands back its device token. Only
    // remember the browser as a private device when that did not happen — and
    // never in the login that just invalidated a device token, so the device type
    // picker can still appear on a later login.
    if (
      !hasDeviceToken &&
      !authCtx.isPrivateDevice &&
      !loginResult.deviceToken &&
      !loginResult.deviceTokenInvalid
    ) {
      await authCtx.login(loginResult.token, { deviceToken: null });
    }
    await authCtx.login(loginResult.token, loginResult);
    if (loginResult.deviceToken) {
      localStorage.removeItem(requestDeviceTokenStorageKey);
      toast.success('Gerätetyp wurde freigegeben und für dieses Gerät aktiviert.');
    }

    // If this device has a pending device-type request that was approved in the
    // meantime, activate it now (swap the temp request token for a real device
    // token). Reads from localStorage, so it is robust to fresh re-logins.
    try {
      await authCtx.checkDeviceActivation?.();
    } catch (_e) {
      // ignore — the background focus/auth triggers will retry
    }
  };

  async function loginHandler(event) {
    event.preventDefault();
    setLoginWarning(null);
    setAccountLocked(null);
    setPasswordChangeRequired(false);

    try {
      const loginResult = await login({
        url: '/api/v1/auth/login',
        method: 'POST',
        headers: authCtx.getAuthHeaders({ withAuth: false, withDevice: true }),
        body: {
          username: username,
          password: password,
          isPrivateDevice: authCtx.isPrivateDevice || undefined,
          // Identifies this browser profile so a device type request that IT
          // approved in the meantime is activated as part of the login.
          installId: getDeviceInstallId(),
        },
      });

      if (loginResult?.needsWebauthnEnrollment) {
        const days = getDaysRemaining(loginResult?.webauthnGracePeriodEndsAt);
        if (days > 0) {
          toast.info(
            `Passkey-Registrierung empfohlen: noch ${days} Tag(e) verbleibend. Bitte in Einstellungen hinterlegen.`
          );
        } else {
          toast.info('Passkey-Registrierung empfohlen: bitte in Einstellungen hinterlegen.');
        }
      }

      await continueLoginFlow(loginResult);
    } catch (err) {
      if (err?.code === 'PASSWORD_CHANGE_REQUIRED') {
        setPasswordChangeRequired(true);
        return;
      }

      if (err?.code === 'WEBAUTHN_REQUIRED') {
        toast.error('Passkey-Anmeldung erforderlich. Passwort-Login ist nicht mehr erlaubt.');
        return;
      }

      if (err?.code === 'ACCOUNT_LOCKED') {
        setAccountLocked(err.data?.data || {});
        return;
      }

      if (err?.code === 'LOGIN_ATTEMPTS_WARNING') {
        setLoginWarning({ remainingAttempts: err.data?.data?.remainingAttempts });
        return;
      }

      // 401 is handled via inline alert below (driven by `useHttp().error`)
      const statusCode = err?.statusCode;
      const message = typeof err?.message === 'string' ? err.message : '';
      const is401 = statusCode === 401 || message.startsWith('401:') || message.includes('401');
      if (is401) return;

      if (isNetworkError(err)) {
        toast.error('Netzwerkfehler beim Anmelden. Bitte überprüfe deine Verbindung.');
        return;
      }

      toast.error('Fehler beim Anmelden.');
    }
  }

  const handlePasswordlessWebauthnLogin = async () => {
    try {
      const startResponse = await webauthnLoginStart();
      const assertion = await startAuthentication({ optionsJSON: startResponse.options });
      const loginResult = await webauthnLoginFinish({
        challenge: startResponse.challenge,
        credential: assertion,
        isPrivateDevice: authCtx.isPrivateDevice || undefined,
        installId: getDeviceInstallId(),
      });
      await continueLoginFlow(loginResult);
      toast.success('Passkey-Anmeldung erfolgreich.');
    } catch (err) {
      toast.error('Fehler bei der Passkey-Anmeldung.');
    }
  };

  const handleDeviceConfirmed = async (deviceToken, newToken, intermediateToken) => {
    // If we have a device token but no new token, we need to complete the login
    if (deviceToken && !newToken) {
      // Complete login with the existing temp token and the new device token
      const token = authCtx.tempToken;
      const tempIntermediateToken = authCtx.tempIntermediateToken;
      authCtx.clearTempToken();

      // Create login data with device token and intermediate token if available
      let loginData = { deviceToken };
      if (tempIntermediateToken && deviceToken) {
        try {
          const decodedDeviceToken = jwt_decode(deviceToken);
          if (decodedDeviceToken.deviceType === 'counter') {
            loginData.intermediateToken = tempIntermediateToken;
          }
        } catch (error) {
          console.error('Error decoding device token:', error);
        }
      }

      await authCtx.login(token, loginData);
    } else if (newToken) {
      // complete login with new token that has proper rights
      let loginData = { deviceToken };
      if (intermediateToken && deviceToken) {
        try {
          const decodedDeviceToken = jwt_decode(deviceToken);
          if (decodedDeviceToken.deviceType === 'counter') {
            loginData.intermediateToken = intermediateToken;
          }
        } catch (error) {
          console.error('Error decoding device token:', error);
        }
      }

      if (deviceToken === null) {
        await authCtx.login(newToken, { deviceToken: null });
      } else {
        await authCtx.login(newToken, loginData);
      }
    } else {
      // Fallback to temp token for private devices
      const token = authCtx.tempToken;
      const tempIntermediateToken = authCtx.tempIntermediateToken;
      authCtx.clearTempToken();

      // Create login data with intermediate token if available and device is counter
      let loginData = { deviceToken };
      if (tempIntermediateToken && deviceToken) {
        try {
          const decodedDeviceToken = jwt_decode(deviceToken);
          if (decodedDeviceToken.deviceType === 'counter') {
            loginData.intermediateToken = tempIntermediateToken;
          }
        } catch (error) {
          console.error('Error decoding device token in fallback:', error);
        }
      }

      if (deviceToken === null) {
        await authCtx.login(token, { deviceToken: null });
      } else {
        await authCtx.login(token, loginData);
      }
    }
  };

  // Check if error is a 401 authentication error
  const isAuthError = error && error.includes('401');

  // Show device confirmation if needed
  if (needsDeviceConfirmation) {
    return (
      <DeviceConfirmation
        allowedDeviceTypes={allowedDeviceTypes}
        onDeviceConfirmed={handleDeviceConfirmed}
      />
    );
  }

  if (!authCtx.isLoggedIn) {
    return (
      <Box sx={{ ...viewRootTextPrimarySx, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Grid
          xs={12}
          container
          rowSpacing={2}
          sx={theme => ({ ...viewRootTextPrimarySx, ...getLoginFormGridSx(theme) })}
          width='95%'
        >
          <form onSubmit={loginHandler} style={{ width: '100%' }}>
            {/* LOGO */}
            <Grid xs={12} style={{ marginBottom: '20px' }}>
              <FunZoneLogo />
            </Grid>

            {/* LOGIN INPUTS */}
            <Grid xs={12}>
              <TextField
                label='Benutzername, E-Mail oder Vor- und Nachname'
                variant='outlined'
                value={username}
                onChange={e => setUsername(e.target.value)}
                fullWidth
                required
                placeholder='z.B. MaxMustermann1, max@example.com oder Max Mustermann'
                id='login-username'
                name='username'
                autoComplete='username'
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-input': {
                    paddingRight: '56px',
                  },
                }}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                label='Passwort'
                variant='outlined'
                type='password'
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                required
                placeholder='Passwort eingeben'
                id='login-password'
                name='password'
                autoComplete='current-password'
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiInputBase-input': {
                    paddingRight: '56px',
                  },
                }}
              />
            </Grid>
            {/* AUTH ERROR ALERTS */}
            {passwordChangeRequired && (
              <Grid xs={12}>
                <Alert severity='warning'>
                  Aus Sicherheitsgründen wurden alle Passwörter zurückgesetzt. Dein bisheriges
                  Passwort gilt nicht mehr. Wir haben dir einen Link zum Vergeben eines neuen
                  Passworts per E-Mail geschickt – über „Passwort vergessen?“ kannst du dir
                  jederzeit einen neuen anfordern.
                </Alert>
              </Grid>
            )}
            {accountLocked && !passwordChangeRequired && (
              <Grid xs={12}>
                <Alert severity='error'>
                  Zu viele fehlgeschlagene Anmeldeversuche. Dein Account ist vorübergehend gesperrt.
                  {' '}Versuche es in {accountLocked.lockoutMinutes || 15} Minuten erneut.
                </Alert>
              </Grid>
            )}
            {loginWarning && !accountLocked && !passwordChangeRequired && (
              <Grid xs={12}>
                <Alert severity='warning'>
                  Falsches Passwort. Noch {loginWarning.remainingAttempts} Versuch{loginWarning.remainingAttempts !== 1 ? 'e' : ''} übrig, danach wird dein Account vorübergehend gesperrt.
                </Alert>
              </Grid>
            )}
            {isAuthError && !loginWarning && !accountLocked && !passwordChangeRequired && (
              <Grid xs={12}>
                <Alert severity='error'>
                  Benutzername, E-Mail oder Passwort ist falsch. Bitte überprüfe deine Eingabe.
                </Alert>
              </Grid>
            )}
            <Grid xs={12} sx={{ marginTop: '20px' }} container>
              {/* PASSWORD RESET BUTTON */}
              <Link to='/request-password-reset' style={{ textDecoration: 'none' }}>
                <Button type='button' variant='text' size='small' sx={{ color: 'text.secondary' }}>
                  <FontAwesomeIcon icon={faKey} style={{ marginRight: '10px' }} />
                  Passwort vergessen?
                </Button>
              </Link>
              {/* LOGIN BUTTON */}
              <Button
                type='submit'
                onClick={loginHandler}
                disabled={password === '' || username === '' || isLoading}
                sx={{ maxWidth: '200px', marginLeft: 'auto' }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Anmelden'}
                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '5px' }} />
              </Button>
            </Grid>
            <Grid xs={12} sx={{ marginTop: '8px' }}>
              <Button
                type='button'
                variant='outlined'
                fullWidth
                onClick={handlePasswordlessWebauthnLogin}
                disabled={isLoading}
              >
                Mit Passkey anmelden
              </Button>
            </Grid>
          </form>
        </Grid>
        <Box sx={{ mt: 'auto', py: 1, display: 'flex', justifyContent: 'center' }}>
          <LegalLinks />
        </Box>
      </Box>
    );
  } else {
    // Restore the view the user was on before lock/session timeout.
    // Use ref so we keep the same path across re-renders (auth updates can trigger a second render before Navigate completes).
    if (returnPathRef.current === null) {
      const storedPath = sessionStorage.getItem(RETURN_PATH_KEY);
      const publicPaths = [
        '/',
        '/login',
        '/rfid-login',
        '/request-password-reset',
        '/reset-password',
      ];
      returnPathRef.current =
        storedPath && storedPath.length > 1 && !publicPaths.includes(storedPath)
          ? storedPath
          : '/welcome';
      // Do not remove from sessionStorage here - React Strict Mode remounts components,
      // and a fresh instance would read null. RequireAuth overwrites the path when we land on a protected route.
    }
    return <Navigate to={returnPathRef.current} replace={true} />;
  }
}

export default Login;
